import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export function AnimatedPyramidLogo({ variant = 'hero' }: { variant?: 'hero' | 'auth' }) {
  const baseSize = variant === 'auth' ? 80 : 120;
  const tagline = variant !== 'auth';

  // 7 flowing bands that sweep upward from left to right
  const bands = [
    { startX: 60, endX: 340, y: 240, thickness: 18 },
    { startX: 80, endX: 320, y: 205, thickness: 16 },
    { startX: 100, endX: 300, y: 172, thickness: 14 },
    { startX: 120, endX: 280, y: 142, thickness: 12 },
    { startX: 140, endX: 260, y: 115, thickness: 10 },
    { startX: 160, endX: 240, y: 92, thickness: 8 },
    { startX: 180, endX: 220, y: 72, thickness: 6 },
  ];

  const letters = 'CapLedger'.split('');
  const taglineText = 'Measure Value. Build Trust.';
  const letterAnimationDuration = 0.15; // Each letter takes 0.15s
  const totalLetterDuration = letters.length * letterAnimationDuration; // ~1.35s
  const taglineTypeDuration = 1.2; // Time to type out tagline
  const delayBeforePyramid = 1.5;
  const pyramidStartTime = totalLetterDuration + taglineTypeDuration + delayBeforePyramid; // ~4.05s
  const pyramidDuration = 0.6 + bands.length * 0.15; // ~1.65s
  const totalCycleDuration = pyramidStartTime + pyramidDuration + 0.5; // ~6.2s

  return (
    <div className="flex flex-col items-center justify-center select-none">
      {/* Pyramid - Animates After Letter Animation + Tagline + 1.5s Delay */}
      <motion.svg
        viewBox="0 0 400 280"
        width={baseSize * 1.8}
        height={baseSize * 1.26}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* Navy blue solid */}
          <linearGradient id="navyFill" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#1F4B8E" />
            <stop offset="100%" stopColor="#2C5BA0" />
          </linearGradient>
        </defs>

        {/* Flowing ribbon bands */}
        {bands.map((band, i) => {
          const controlY = band.y - 30; // Control point for upward curve

          return (
            <motion.path
              key={i}
              d={`M ${band.startX} ${band.y}
                  Q ${(band.startX + band.endX) / 2} ${controlY}, ${band.endX} ${band.y}
                  Q ${(band.startX + band.endX) / 2} ${controlY + band.thickness}, ${band.startX} ${band.y + band.thickness}
                  Z`}
              fill="url(#navyFill)"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{
                pathLength: [0, 0, 1, 1],
                opacity: [0, 0, 1, 1],
              }}
              transition={{
                duration: totalCycleDuration,
                delay: i * 0.15,
                times: [
                  0,
                  pyramidStartTime / totalCycleDuration,
                  (pyramidStartTime + 0.6) / totalCycleDuration,
                  1,
                ],
                repeat: Infinity,
                ease: 'easeOut',
              }}
            />
          );
        })}

        {/* Upward arrow at the peak */}
        <motion.path
          d="M 200 45 L 210 60 L 190 60 Z"
          fill="#1F4B8E"
          initial={{ scale: 0, opacity: 0, y: 10 }}
          animate={{
            scale: [0, 0, 1.3, 1],
            opacity: [0, 0, 1, 1],
            y: [10, 10, 0, 0],
          }}
          style={{ transformOrigin: '200px 52px' }}
          transition={{
            duration: totalCycleDuration,
            delay: bands.length * 0.15,
            times: [
              0,
              pyramidStartTime / totalCycleDuration,
              (pyramidStartTime + 0.4) / totalCycleDuration,
              1,
            ],
            repeat: Infinity,
            ease: 'easeOut',
          }}
        />
      </motion.svg>

      {/* Wordmark + Tagline */}
      <motion.div className="text-center mt-3">
        <h1
          className={`font-serif font-semibold ${variant === 'auth' ? 'text-2xl' : 'text-4xl md:text-5xl'}`}
        >
          {letters.map((letter, i) => (
            <motion.span
              key={i}
              className="inline-block"
              initial={{ color: '#94a3b8' }} // slate-400
              animate={{
                color: [
                  '#94a3b8', // slate-400 (initial)
                  '#94a3b8', // wait
                  '#1F4B8E', // navy (animate to)
                  '#1F4B8E', // hold
                ],
              }}
              transition={{
                duration: totalCycleDuration,
                delay: i * letterAnimationDuration,
                times: [
                  0,
                  (i * letterAnimationDuration) / totalCycleDuration,
                  (i * letterAnimationDuration + 0.3) / totalCycleDuration,
                  1,
                ],
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              {letter}
            </motion.span>
          ))}
        </h1>
        {tagline && (
          <p className="mt-1 text-capledger-teal font-medium text-sm md:text-base tracking-wide h-6">
            <motion.span
              initial={{ width: 0 }}
              animate={{
                width: [0, 0, '100%', '100%'],
              }}
              transition={{
                duration: totalCycleDuration,
                times: [
                  0,
                  totalLetterDuration / totalCycleDuration,
                  (totalLetterDuration + taglineTypeDuration) / totalCycleDuration,
                  1,
                ],
                repeat: Infinity,
                ease: 'linear',
              }}
              className="inline-block overflow-hidden whitespace-nowrap align-top"
            >
              {taglineText}
            </motion.span>
          </p>
        )}
      </motion.div>
    </div>
  );
}

/* ---------------- Landing Hero ---------------- */
export function LandingHero() {
  const navigate = useNavigate();

  return (
    <section className="min-h-screen flex flex-col items-center justify-center bg-capledger-mist text-center px-6">
      <motion.div
        animate={{ scale: [1, 1.02, 1] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
      >
        <AnimatedPyramidLogo variant="hero" />
      </motion.div>

      <motion.div
        className="mt-10 max-w-2xl text-slate-600"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, duration: 0.6 }}
      >
        <p className="text-lg md:text-xl leading-relaxed">
          Intelligent valuation and equity governance built for trust, compliance, and growth.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
          <motion.div whileHover={{ scale: 1.04 }}>
            <Button
              size="lg"
              onClick={() => navigate('/auth?mode=signup')}
              className="px-8 py-6 text-lg font-semibold bg-capledger-navy hover:bg-capledger-navy-dark text-white shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Sign Up Free
            </Button>
          </motion.div>
          <motion.div whileHover={{ scale: 1.04 }}>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/auth?mode=signin')}
              className="px-8 py-6 text-lg font-semibold border-2 border-capledger-navy text-capledger-navy hover:bg-capledger-navy hover:text-white transition-all duration-200"
            >
              Sign In
            </Button>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}

/* ---------------- Auth Screen (Login / Signup) ---------------- */
export function AuthScreen({ type }: { type: 'login' | 'signup' }) {
  return (
    <section className="min-h-screen grid place-items-center bg-gradient-to-br from-slate-50 to-slate-200 px-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center">
          <AnimatedPyramidLogo variant="auth" />
        </div>
        <div className="mt-6 bg-white/90 backdrop-blur p-8 rounded-xl shadow-md border border-slate-200">
          <h2 className="text-center text-2xl font-semibold text-slate-700 mb-6">
            {type === 'login' ? 'Welcome Back' : 'Create Your Account'}
          </h2>
          <form className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              className="w-full border rounded px-3 py-2 outline-none focus:ring-2 focus:ring-capledger-teal"
            />
            <input
              type="password"
              placeholder="Password"
              className="w-full border rounded px-3 py-2 outline-none focus:ring-2 focus:ring-capledger-teal"
            />
            <button
              type="submit"
              className="w-full bg-capledger-teal text-white py-2 rounded font-medium hover:bg-teal-600"
            >
              {type === 'login' ? 'Sign In' : 'Sign Up'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
